import type { EvaluationJobLiveProgress, EvaluationJobPreviewData, EvaluationRunAgainSeed } from '../types/evaluationJob';
import {
  mergeEvaluatorParamsFormForMode,
  normalizeEvaluationMode,
  parseEvaluatorParamsForm,
  type DatasetSplit,
} from '../types/evaluation';
import { normalizeOptimizationDatasetNames } from './optimizationDatasetName';
import { canCancelJob, isActiveJobStatus } from './trainingJobRetry';

const TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);

export const EVALUATION_JOB_RETRY_STATUSES = ['failed', 'cancelled'] as const;

export function canRetryEvaluationJob(status: string | undefined | null): boolean {
  const normalized = (status || '').toLowerCase().trim();
  return (EVALUATION_JOB_RETRY_STATUSES as readonly string[]).includes(normalized);
}

export function isTerminalEvaluationJobStatus(status: string | undefined | null): boolean {
  return TERMINAL_STATUSES.has((status || '').toLowerCase().trim());
}

export function isActiveEvaluationJobStatus(status: string | undefined | null): boolean {
  return isActiveJobStatus(status);
}

export function canCancelEvaluationJob(status: string | undefined | null): boolean {
  return canCancelJob(status);
}

function readPercent(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, n));
}

export function unwrapEvaluationJobRow(row: unknown): Record<string, unknown> {
  if (!row || typeof row !== 'object') return {};
  let cur = row as Record<string, unknown>;
  for (let depth = 0; depth < 3; depth += 1) {
    const inner = cur.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const record = inner as Record<string, unknown>;
    if ('id' in record || 'status' in record || 'progress_percent' in record || 'result' in record) {
      cur = record;
    } else {
      break;
    }
  }
  return cur;
}

/** Job UUID for evaluation/jobs endpoints. */
export function resolveEvaluationJobRecordId(job: unknown): string {
  const row = unwrapEvaluationJobRow(job);
  const id = String(row.id ?? '').trim();
  if (id) return id;
  return String(row.job_id ?? '').trim();
}

export function parseEvaluationJobLiveProgress(job: unknown): EvaluationJobLiveProgress | null {
  const row = unwrapEvaluationJobRow(job);
  const raw = row.progress;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const rp = raw as Record<string, unknown>;
  const parsed: EvaluationJobLiveProgress = {
    stage: typeof rp.stage === 'string' ? rp.stage.trim() : undefined,
    substage: typeof rp.substage === 'string' ? rp.substage.trim() : undefined,
    stage_iteration: readPositiveInt(rp.stage_iteration),
    stage_max_iterations: readPositiveInt(rp.stage_max_iterations),
    progress_percent: readPercent(rp.progress_percent),
    message: typeof rp.message === 'string' ? rp.message.trim() : undefined,
  };
  const hasData = Object.values(parsed).some((v) => v != null && v !== '');
  return hasData ? parsed : null;
}

function readPositiveInt(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

function isEvaluationQueueLikePhase(value: unknown): boolean {
  const phase = String(value || '').toLowerCase().trim();
  if (!phase) return false;
  return /queue|queued|pending|wait/.test(phase);
}

function isExplicitEvaluationExecutionSignal(value: unknown): boolean {
  const signal = String(value || '').toLowerCase().trim();
  if (!signal || isEvaluationQueueLikePhase(signal)) return false;
  // The API may optimistically expose a generic `running`/`in_progress` state
  // while a job is still waiting for a worker. Only domain-specific work is a
  // reliable start signal when progress is still 0 and started_at is absent.
  return /evaluat|process|scor|batch|dialog|generat|compar|metric/.test(signal);
}

/** True when the job has actually started executing (not just waiting for a worker slot). */
export function hasEvaluationJobExecutionStarted(job: unknown): boolean {
  const row = unwrapEvaluationJobRow(job);
  const startedAt = row.started_at;
  if (typeof startedAt === 'string' && startedAt.trim()) return true;

  const topPercent = readPercent(row.progress_percent);
  if (topPercent != null && topPercent > 0) return true;

  const live = parseEvaluationJobLiveProgress(row);
  if (live?.progress_percent != null && live.progress_percent > 0) return true;
  if (live?.stage_iteration != null && live.stage_iteration > 0) return true;

  if (isExplicitEvaluationExecutionSignal(live?.stage)) return true;
  if (isExplicitEvaluationExecutionSignal(live?.substage)) return true;
  if (isExplicitEvaluationExecutionSignal(live?.message)) return true;
  if (isExplicitEvaluationExecutionSignal(row.phase)) return true;

  return false;
}

/** Raw API status (for cancel/retry/terminal checks). */
export function resolveEvaluationJobRawStatus(job: unknown): string {
  return String(unwrapEvaluationJobRow(job).status || '').toLowerCase().trim();
}

/**
 * User-facing status label source. Maps API `running` with 0% / no start time to `queued`.
 */
export function resolveEvaluationJobDisplayStatus(job: unknown): string {
  const row = unwrapEvaluationJobRow(job);
  const status = String(row.status || '').toLowerCase().trim();
  const phase = String(row.phase || '').toLowerCase().trim();

  if (status === 'queued' || status === 'pending' || isEvaluationQueueLikePhase(phase)) {
    return 'queued';
  }

  if (status === 'running' && !hasEvaluationJobExecutionStarted(row)) {
    return 'queued';
  }

  return status;
}

export function formatEvaluationJobDisplayStatusLabel(status: string | undefined | null): string {
  const normalized = String(status || '').toLowerCase().trim();
  if (!normalized) return '—';
  if (normalized === 'queued') return 'Queued';
  if (normalized === 'pending') return 'Pending';
  if (normalized === 'running') return 'Running';
  if (normalized === 'cancelling') return 'Cancelling';
  if (normalized === 'succeeded') return 'Succeeded';
  if (normalized === 'failed') return 'Failed';
  if (normalized === 'cancelled') return 'Cancelled';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatSnakeLabel(value: string): string {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function buildDistinctStageLabel(stage?: string, substage?: string): string | null {
  const labels = [stage, substage]
    .filter((part): part is string => Boolean(part?.trim()))
    .map((part) => formatSnakeLabel(part));

  const uniqueLabels: string[] = [];
  for (const label of labels) {
    const normalized = label.toLowerCase();
    if (!uniqueLabels.some((existing) => existing.toLowerCase() === normalized)) {
      uniqueLabels.push(label);
    }
  }

  return uniqueLabels.length > 0 ? uniqueLabels.join(' · ') : null;
}

export function resolveEvaluationJobProgressDisplay(job: unknown): {
  percent: number;
  message: string | null;
  meta: string | null;
} {
  const row = unwrapEvaluationJobRow(job);
  const rawStatus = String(row.status || '').toLowerCase();
  const displayStatus = resolveEvaluationJobDisplayStatus(row);
  const topPercent = readPercent(row.progress_percent);
  const live = parseEvaluationJobLiveProgress(row);

  if (rawStatus === 'succeeded' || displayStatus === 'succeeded') {
    return { percent: 100, message: live?.message || 'Evaluation completed.', meta: null };
  }

  let percent = topPercent ?? live?.progress_percent ?? 0;
  if (displayStatus === 'queued') {
    percent = topPercent ?? 0;
  }

  const message =
    live?.message ||
    (displayStatus === 'queued'
      ? 'Waiting in queue…'
      : rawStatus === 'cancelling'
        ? 'Cancelling…'
        : null);

  const metaParts: string[] = [];
  if (live?.stage_iteration != null && live?.stage_max_iterations != null) {
    metaParts.push(`Stage ${live.stage_iteration}/${live.stage_max_iterations}`);
  }
  const stageLabel = buildDistinctStageLabel(live?.stage, live?.substage);
  if (stageLabel) metaParts.push(stageLabel);

  return {
    percent: Math.round(percent),
    message,
    meta: metaParts.length > 0 ? metaParts.join(' · ') : null,
  };
}

export function extractEvaluationJobIdFromResponse(response: unknown): string | undefined {
  const envelope = response as { data?: { id?: string }; id?: string } | null | undefined;
  const jobPayload = envelope?.data ?? envelope;
  if (jobPayload && typeof jobPayload === 'object') {
    const id = (jobPayload as { id?: string }).id;
    return typeof id === 'string' && id.trim() ? id.trim() : undefined;
  }
  return undefined;
}

/** Unwrap preview payload from API envelope or nested `.data`. */
export function parseEvaluationRunAgainSeed(job: unknown): EvaluationRunAgainSeed {
  const row = unwrapEvaluationJobRow(job);
  const evaluationMode = normalizeEvaluationMode(row.evaluation_mode);
  const splitRaw = String(row.dataset_split || '').toLowerCase();
  const datasetSplit: DatasetSplit = splitRaw === 'train' ? 'train' : 'validation';

  let datasetNames = normalizeOptimizationDatasetNames(row.dataset_name ?? row.dataset_names);
  const request = row.request;
  if (datasetNames.length === 0 && request && typeof request === 'object' && !Array.isArray(request)) {
    const req = request as Record<string, unknown>;
    datasetNames = normalizeOptimizationDatasetNames(req.dataset_name ?? req.dataset_names);
  }

  const batchRaw = row.batch_size ?? (request as Record<string, unknown> | undefined)?.batch_size;
  let batchSize: string | undefined;
  if (typeof batchRaw === 'number' && Number.isFinite(batchRaw)) {
    batchSize = String(Math.floor(batchRaw));
  } else if (typeof batchRaw === 'string' && batchRaw.trim()) {
    batchSize = batchRaw.trim();
  }

  const evaluatorRaw =
    row.evaluator_params ??
    (request && typeof request === 'object' ? (request as Record<string, unknown>).evaluator_params : undefined);

  return {
    datasetNames,
    datasetSplit,
    batchSize,
    evaluationMode,
    evaluatorParams: mergeEvaluatorParamsFormForMode(evaluationMode, parseEvaluatorParamsForm(evaluatorRaw)),
  };
}

export function mergeEvaluationJobHistoryRows(
  listRows: unknown[],
  liveById: ReadonlyMap<string, Record<string, unknown>>
): Record<string, unknown>[] {
  return (Array.isArray(listRows) ? listRows : []).map((row) => {
    const base = unwrapEvaluationJobRow(row);
    const id = String(base.id || '').trim();
    const live = id ? liveById.get(id) : undefined;
    return live ? { ...base, ...live } : base;
  });
}

export function shouldTrackEvaluationJobRow(job: unknown): boolean {
  const row = unwrapEvaluationJobRow(job);
  const status = String(row.status || '').toLowerCase().trim();
  if (isTerminalEvaluationJobStatus(status)) return false;
  return isActiveEvaluationJobStatus(status);
}

export function unwrapEvaluationPreviewPayload(raw: unknown): EvaluationJobPreviewData | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const inner = record.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const nested = inner as Record<string, unknown>;
    if (
      'expected_total_tokens' in nested ||
      'expected_input_tokens' in nested ||
      'predicted_total_cost_usd' in nested ||
      'n_dialogs' in nested
    ) {
      return nested as EvaluationJobPreviewData;
    }
  }
  if (
    'expected_total_tokens' in record ||
    'expected_input_tokens' in record ||
    'predicted_total_cost_usd' in record ||
    'n_dialogs' in record
  ) {
    return record as EvaluationJobPreviewData;
  }
  return null;
}
